import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'CosmiCards';
const DEFAULT_TITLE = 'CosmiCards - AI Tarot Readings, Occult Wisdom & Cosmic RPG';
const DEFAULT_DESCRIPTION = 'Explore AI-powered tarot readings, occult symbolism, birth chart astrology, and an epic cosmic RPG. Rider-Waite-Smith & Crowley Thoth decks, 17+ spreads, dream interpretation, and a vibrant social community. Free to play.';
const DEFAULT_IMAGE = 'https://www.t3b3tu.com/og-image.jpg';
const SITE_URL = 'https://www.t3b3tu.com';

/**
 * SEO Component - Manages all meta tags for pages
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   image="/path/to/image.png"
 *   type="article"
 *   url="/page-path"
 *   keywords="keyword1, keyword2"
 *   noindex={false}
 * />
 */
export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  type = 'website',
  url = '',
  keywords = '',
  noindex = false,
  structuredData = null,
  article = null,
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const pageUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const pageImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  
  const defaultKeywords = 'tarot, astrology, occult, ai tarot reading, thoth tarot, rider waite smith, birth chart, horoscope, zodiac, numerology, cardology, cosmic quest, rpg game, daily tarot, free tarot reading, ai divination, esoteric, mysticism, tarot community, social tarot, occult symbolism, crowley thoth, kabbalah, tarot spreads, celtic cross';
  const allKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:site" content="@cosmicards" />
      <meta name="twitter:creator" content="@cosmicards" />
      
      {/* Article specific (for blog posts, readings, etc.) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags && article.tags.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for common pages
export const HomeSEO = () => (
  <SEO
    title="AI Tarot & Occult Astrology Platform"
    description="CosmiCards — free AI-powered tarot readings with Rider-Waite-Smith & Crowley Thoth decks, birth chart astrology, occult symbolism, dream interpretation, and a cosmic RPG. Join a growing social community of mystics."
    url="/"
    keywords="free tarot reading online, ai tarot, daily horoscope, birth chart calculator, occult wisdom, social tarot community, esoteric astrology"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "CosmiCards",
      "url": "https://www.t3b3tu.com",
      "description": "AI-powered tarot, astrology, and occult wisdom platform with social community features",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.t3b3tu.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }}
  />
);

export const TarotSEO = ({ spreadName, spreadDescription }) => (
  <SEO
    title={spreadName || "AI Tarot Readings — RWS & Thoth Decks"}
    description={spreadDescription || "Get accurate AI-powered tarot readings with Rider-Waite-Smith and Crowley Thoth decks. 17+ spreads including Celtic Cross, love, career, and occult-themed layouts. Share readings on social media."}
    url="/tarot"
    keywords="ai tarot reading, celtic cross spread, thoth tarot, rider waite tarot, love tarot, career tarot, yes no tarot, occult divination, share tarot social media"
    type="article"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "CosmiCards AI Tarot Readings",
      "description": "AI-powered tarot readings with RWS and Thoth decks, 17+ spreads",
      "provider": {
        "@type": "Organization",
        "name": "CosmiCards"
      },
      "serviceType": "Tarot Reading",
      "areaServed": "Worldwide"
    }}
  />
);

export const BirthChartSEO = () => (
  <SEO
    title="Birth Chart Calculator — Free AI Natal Chart"
    description="Calculate your complete birth chart with AI-powered interpretations. Planetary positions, houses, aspects, and occult astrological insights. Free natal chart analysis with social sharing."
    url="/birth-chart"
    keywords="birth chart calculator, natal chart, ai astrology, planetary positions, houses, aspects, free birth chart, occult astrology, esoteric chart"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CosmiCards Birth Chart Calculator",
      "applicationCategory": "LifestyleApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }}
  />
);

export const RPGGameSEO = () => (
  <SEO
    title="Cosmic Quest RPG"
    description="Embark on an epic RPG adventure! Battle cosmic monsters, level up your hero, collect gear, join PVP arenas, and explore endless dungeons in this browser-based RPG."
    url="/cosmic-quest"
    keywords="browser rpg, free rpg game, cosmic quest, monster battles, pvp arena, dungeon crawler"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Cosmic Quest",
      "description": "Browser-based RPG with cosmic battles and endless progression",
      "genre": ["RPG", "Browser Game", "Adventure"],
      "gamePlatform": "Web Browser",
      "applicationCategory": "Game",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }}
  />
);

export const SolitaireSEO = () => (
  <SEO
    title="Cosmic Solitaire"
    description="Play free Solitaire with a cosmic twist! Features sound effects, haptic feedback, game replays, and leaderboards. The classic card game reimagined."
    url="/games/solitaire"
    keywords="free solitaire, online solitaire, klondike solitaire, card game, solitaire replay"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Cosmic Solitaire",
      "description": "Classic Klondike Solitaire with modern features",
      "genre": ["Card Game", "Puzzle"],
      "gamePlatform": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }}
  />
);

export const MahjongSEO = () => (
  <SEO
    title="Cosmic Mahjong"
    description="Play free Mahjong Solitaire online! Match tiles, beat the clock, and climb the leaderboards. Beautiful cosmic-themed tiles and relaxing gameplay."
    url="/games/mahjong"
    keywords="free mahjong, mahjong solitaire, tile matching game, online mahjong"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Cosmic Mahjong",
      "description": "Tile-matching Mahjong Solitaire game",
      "genre": ["Puzzle", "Tile Matching"],
      "gamePlatform": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }}
  />
);

export const CommunitySEO = () => (
  <SEO
    title="Community"
    description="Join the CosmiCards community! Share readings, connect with fellow cosmic explorers, discuss astrology and tarot, and make friends on your spiritual journey."
    url="/community"
    keywords="tarot community, astrology forum, spiritual community, cosmic friends"
  />
);

export const ProfileSEO = ({ userName, userBio }) => (
  <SEO
    title={userName ? `${userName}'s Profile` : "User Profile"}
    description={userBio || `View ${userName || 'this user'}'s cosmic profile, readings, and achievements on CosmiCards.`}
    type="profile"
    noindex={true}
  />
);

export const DreamInterpretationSEO = () => (
  <SEO
    title="AI Dream Interpretation — Occult Dream Analysis"
    description="Decode your dreams with AI-powered occult dream interpretation. Discover hidden meanings, archetypes, and cosmic messages in your dreams. Share insights with the community."
    url="/dreams"
    keywords="ai dream interpretation, dream meaning, dream analysis, occult dream symbols, dream dictionary, ai dream interpreter, lucid dreaming"
  />
);

export const CardOfTheDaySEO = () => (
  <SEO
    title="Card of the Day — Free Daily Tarot"
    description="Draw your free daily tarot card and receive AI-powered cosmic guidance. Choose from Rider-Waite-Smith or Crowley Thoth decks. Share your card on social media."
    url="/card-of-the-day"
    keywords="daily tarot card, card of the day, free tarot card, daily guidance, ai tarot daily, share tarot"
  />
);

export const LearnTarotSEO = ({ deckName }) => (
  <SEO
    title={deckName === 'thoth' ? "Learn the Crowley Thoth Tarot — All 78 Cards" : "Learn Tarot — Rider-Waite-Smith Complete Guide"}
    description={deckName === 'thoth'
      ? "Master the Crowley-Harris Thoth Tarot — all 78 cards with Qabalistic symbolism, Tree of Life paths, astrological correspondences, and Thelemic interpretations. High-quality card images."
      : "Complete guide to the Rider-Waite-Smith tarot deck. All 78 cards with imagery, symbolism, archetypes, upright & reversed meanings. Interactive card browser with high-quality images."
    }
    url="/learn"
    keywords="learn tarot cards, tarot card meanings, thoth tarot guide, rider waite smith meanings, major arcana, minor arcana, tarot symbolism, occult tarot, kabbalah tarot, tarot for beginners"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Course",
      "name": deckName === 'thoth' ? "Crowley Thoth Tarot Complete Guide" : "Rider-Waite-Smith Tarot Complete Guide",
      "description": "Interactive tarot learning guide with all 78 cards, symbolism, and interpretations",
      "provider": {
        "@type": "Organization",
        "name": "CosmiCards"
      },
      "isAccessibleForFree": true
    }}
  />
);

export const OracleSEO = () => (
  <SEO
    title="AI Oracle — Ask the Cosmic Oracle"
    description="Consult the AI Oracle for cosmic guidance. Ask any question and receive mystical AI-powered answers drawn from tarot, astrology, and occult wisdom."
    url="/oracle"
    keywords="ai oracle, ask oracle, cosmic oracle, ai divination, mystical guidance, occult oracle, fortune telling ai"
  />
);

export const CardologySEO = () => (
  <SEO
    title="Cardology — Birth Card Calculator"
    description="Discover your personal birth cards and life path through the ancient science of cardology. AI-powered interpretations of your card destiny."
    url="/birth-cards"
    keywords="cardology, birth cards, playing card astrology, card of destiny, life spread, occult cards"
  />
);

export const GamesHubSEO = () => (
  <SEO
    title="Cosmic Games — Solitaire, Mahjong & RPG"
    description="Play free cosmic-themed games! Solitaire, Mahjong, and the Cosmic Quest RPG. Earn rewards, climb leaderboards, and connect with the community."
    url="/games"
    keywords="free online games, cosmic solitaire, cosmic mahjong, browser rpg, card games"
  />
);

export const CompatibilitySEO = () => (
  <SEO
    title="Zodiac Compatibility — AI Astrology Match"
    description="Check your zodiac compatibility with AI-powered astrology. Compare birth charts, synastry analysis, and relationship insights based on occult astrological traditions."
    url="/compatibility"
    keywords="zodiac compatibility, synastry, astrology match, relationship astrology, ai compatibility, love astrology"
  />
);

export default SEO;
